import { Link } from "react-router-dom";
import type { VenueDetail } from "../../../mocks/allVenues";

interface SelectedVenuePopupProps {
  venue: VenueDetail;
  onClose: () => void;
  onDirections: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  historical: "bg-amber-100 text-amber-700",
  food: "bg-red-100 text-red-700",
  entertainment: "bg-violet-100 text-violet-700",
  accommodation: "bg-sky-100 text-sky-700",
  nature: "bg-emerald-100 text-emerald-700",
  sightseeing: "bg-orange-100 text-orange-700",
};

export default function SelectedVenuePopup({ venue, onClose, onDirections }: SelectedVenuePopupProps) {
  const catColor = CATEGORY_COLORS[venue.categorySlug] || "bg-stone-100 text-stone-700";

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 w-full max-w-md px-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden">
        <div className="flex gap-0">
          {/* Image */}
          <div className="w-28 flex-shrink-0">
            <img
              src={venue.image}
              alt={venue.name}
              className="w-full h-full object-cover object-top"
              style={{ minHeight: "110px" }}
            />
          </div>

          {/* Content */}
          <div className="flex-1 p-3">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${catColor}`}>
                  {venue.category}
                </span>
                <h3 className="text-sm font-bold text-anthracite-900 leading-snug">{venue.name}</h3>
              </div>
              <button
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
              >
                <i className="ri-close-line text-stone-500 text-xs" />
              </button>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-amber-400 text-xs" />
                <span className="text-xs font-semibold text-anthracite-700">{venue.rating}</span>
                <span className="text-xs text-stone-400 ml-0.5">({venue.reviews.toLocaleString()})</span>
              </div>
              <span className="text-stone-300">·</span>
              <span className={`text-xs font-medium ${venue.isOpen ? "text-emerald-600" : "text-red-500"}`}>
                {venue.isOpen ? "Açık" : "Kapalı"}
              </span>
              {venue.isEditorPick && (
                <>
                  <span className="text-stone-300">·</span>
                  <span className="text-xs text-amber-600 font-medium whitespace-nowrap">Editör</span>
                </>
              )}
            </div>

            <p className="text-xs text-stone-500 leading-relaxed truncate mb-2.5">{venue.address}</p>

            <div className="flex items-center gap-2">
              <button
                onClick={onDirections}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-lg text-xs font-medium text-anthracite-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-navigation-line text-sm" />
                Yol Tarifi
              </button>
              <Link
                to={`/venue/${venue.id}`}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-forest-600 hover:bg-forest-500 rounded-lg text-xs font-medium text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-sm" />
                Detaylar
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
